import { useCallback, useMemo, useState } from "react";

import { AssessmentStatusTable } from "./AssessmentStatusTable";
import { EmptyAssessmentDashboard } from "./EmptyAssessmentDashboard";
import {
  buildOpenForSubmissionCSVData,
  mapAllAssessmentTableRow,
  partitionAllAssessmentDashboardRows,
  translateAllAssessmentReport,
} from "./utils";

import { AssessmentStatus } from "api/api-data-types";
import {
  serverGetAllAssessmentReport,
  serverGetAllAssessmentTable,
} from "api/api-server-assessment";
import { t } from "i18n/i18n";
import { useAppSelector } from "store/index";
import { selectCurrentSectionId } from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";

export const AllAssessmentDashboard = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const [refetchKey, setRefetchKey] = useState(0);
  const refetch = useCallback(() => setRefetchKey((k) => k + 1), []);

  const { data: allAssessmentTableRows, isLoading } = usePromiseEffect(async () => {
    return sectionId ? serverGetAllAssessmentTable(sectionId) : null;
  }, [sectionId, refetchKey]);

  const allAssessmentsTableRows = useMemo(() => {
    if (!allAssessmentTableRows) {
      return [];
    }

    return allAssessmentTableRows.map(mapAllAssessmentTableRow);
  }, [allAssessmentTableRows]);

  const { openForSubmissionsRows, assessableRows, assessedRows } = useMemo(
    () => partitionAllAssessmentDashboardRows(allAssessmentsTableRows),
    [allAssessmentsTableRows],
  );

  const getAssessableExportData = useCallback(async () => {
    const csvData = await serverGetAllAssessmentReport({
      section_id: sectionId!,
      report_filter: AssessmentStatus.ASSESSABLE,
    });
    return translateAllAssessmentReport(csvData, true);
  }, [sectionId]);

  const getAssessedExportData = useCallback(async () => {
    const csvData = await serverGetAllAssessmentReport({
      section_id: sectionId!,
      report_filter: AssessmentStatus.ASSESSED,
    });
    return translateAllAssessmentReport(csvData, true);
  }, [sectionId]);

  const openForSubmissionsExportData = useMemo(
    () => buildOpenForSubmissionCSVData(openForSubmissionsRows),
    [openForSubmissionsRows],
  );

  if (!sectionId) {
    return null;
  }

  const hasNoData =
    !isLoading &&
    assessableRows.length === 0 &&
    assessedRows.length === 0 &&
    openForSubmissionsRows.length === 0;

  if (hasNoData) {
    return <EmptyAssessmentDashboard />;
  }

  return (
    <main className="assessment-dashboard">
      <AssessmentStatusTable
        emptyDescription={t("assessment_dashboard.all.empty_state.assessable")}
        exportData={getAssessableExportData}
        sectionId={sectionId}
        tableRows={assessableRows}
        title={t("assessment_dashboard.all.ready_for_assessment")}
        onRefetch={refetch}
      />

      <AssessmentStatusTable
        emptyDescription={t("assessment_dashboard.all.empty_state.assessed")}
        exportData={getAssessedExportData}
        sectionId={sectionId}
        tableRows={assessedRows}
        title={t("assessment_dashboard.all.assessed")}
        onRefetch={refetch}
      />

      <AssessmentStatusTable
        emptyDescription={t("assessment_dashboard.all.empty_state.open_submissions")}
        exportData={openForSubmissionsExportData}
        sectionId={sectionId}
        tableRows={openForSubmissionsRows}
        title={t("assessment_dashboard.all.open_for_submissions")}
        hideMarkCompleteMenu
      />
    </main>
  );
};
